import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { RefreshCw, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import StatusChip from "@/components/StatusChip";
import { formatAmount, asText } from "@/lib/format";

const TILES = [
  { key: "total", label: "Total Files", testId: TID.batchSummaryTotal, tone: "neutral" },
  { key: "processing", label: "Processing", testId: TID.batchSummaryProcessing, tone: "info" },
  { key: "ready", label: "Ready", testId: TID.batchSummaryReady, tone: "success" },
  { key: "needs_review", label: "Needs Review", testId: TID.batchSummaryNeedsReview, tone: "warn" },
  { key: "blocked", label: "Blocked", testId: TID.batchSummaryBlocked, tone: "error" },
  { key: "failed", label: "OCR Failed", testId: TID.batchSummaryFailed, tone: "error" },
];

const TONE_CLS = {
  neutral: "bg-archival-surface border-archival text-archival-primary",
  info: "bg-blue-50 border-blue-200 text-blue-800",
  success: "bg-emerald-50 border-emerald-200 text-emerald-800",
  warn: "bg-amber-50 border-amber-200 text-amber-800",
  error: "bg-red-50 border-red-200 text-red-800",
};

export default function BatchProcessing() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const [b, c] = await Promise.all([
        api.getBatch(batchId),
        api.listCandidates({ batch_id: batchId }),
      ]);
      setBatch(b);
      setCandidates(c);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  const counts = batch
    ? {
        total: batch.total_files || 0,
        processing: Math.max(
          (batch.total_files || 0) - (batch.processed_count || 0) - (batch.failed_count || 0),
          0
        ),
        ready: batch.ready_count || 0,
        needs_review: batch.needs_review_count || 0,
        blocked: batch.blocked_count || 0,
        failed: batch.failed_count || 0,
      }
    : { total: 0, processing: 0, ready: 0, needs_review: 0, blocked: 0, failed: 0 };

  if (!batch) {
    return <div className="text-archival-secondary">Loading batch…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[12px] text-archival-muted font-mono">BATCH · {batch.id.slice(0, 8)}</div>
          <h1 className="text-[28px] font-semibold text-archival-primary leading-tight tracking-tight">
            Batch Processing
          </h1>
          <p className="text-[14px] text-archival-secondary mt-1">
            {batch.notes ? batch.notes : "Watching candidate processing in real time."} ·{" "}
            <StatusChip status={batch.status === "COMPLETED" ? "SENT_TO_EC" : "OCR_PROCESSING"} label={batch.status} />
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            data-testid={TID.batchRefresh}
            onClick={async () => {
              setRefreshing(true);
              await load();
              setRefreshing(false);
            }}
            className="border-archival bg-white"
          >
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </Button>
          <Button
            data-testid={TID.batchGoToQueue}
            onClick={() => navigate(`/queue?batch_id=${batchId}`)}
            className="bg-action text-white"
            style={{ backgroundColor: "var(--action)" }}
          >
            Go to Review Queue
            <ArrowRight size={14} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {TILES.map((t) => (
          <div
            key={t.key}
            data-testid={t.testId}
            className={`rounded-md border p-3 ${TONE_CLS[t.tone]}`}
          >
            <div className="text-[11px] uppercase tracking-wider font-semibold opacity-80">
              {t.label}
            </div>
            <div className="text-[28px] font-mono mt-1 leading-none">{counts[t.key]}</div>
          </div>
        ))}
      </div>

      <div className="rounded-md border border-archival bg-archival-surface overflow-hidden">
        <div className="px-4 py-2.5 border-b border-archival flex items-center justify-between">
          <div className="text-[13px] font-semibold text-archival-primary">Files in this batch</div>
          <div className="text-[11px] text-archival-muted font-mono">{candidates.length} candidate(s)</div>
        </div>
        <ul>
          {candidates.length === 0 && (
            <li className="px-4 py-8 text-center text-archival-muted text-[13px]">
              No candidates yet.
            </li>
          )}
          {candidates.map((c) => (
            <li
              key={c.id}
              className="grid grid-cols-12 items-center gap-3 px-4 py-3 border-b border-archival last:border-b-0 hover:bg-archival-soft transition-colors"
            >
              <div className="col-span-4 min-w-0">
                <div className="text-[13px] text-archival-primary truncate">{c.original_filename}</div>
                <div className="text-[11px] text-archival-muted font-mono">{c.id.slice(0, 8)}</div>
              </div>
              <div className="col-span-3 text-[13px] text-archival-secondary truncate">
                {c.suggested_vendor_name || c.extracted_vendor_name || "—"}
              </div>
              <div className="col-span-2 text-[13px] font-mono text-archival-primary">
                {formatAmount(c.total_amount, c.currency)}
              </div>
              <div className="col-span-2">
                <StatusChip status={c.status} />
                {c.error_message && (
                  <div className="text-[10px] text-red-700 mt-0.5 truncate" title={asText(c.error_message)}>
                    {asText(c.error_message)}
                  </div>
                )}
              </div>
              <div className="col-span-1 text-right">
                <Link
                  to={`/candidates/${c.id}`}
                  className="text-[12px] text-archival-secondary hover:text-archival-primary underline-offset-4 hover:underline"
                >
                  Open
                </Link>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
